import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios'; // Import axios for tracking requests

const CountdownBanner = ({ sku }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // List of possible coupon codes
  const couponCodes = [
    'X1G2T5', 'A6W5X2', 'Y7Z2O3', 'P9X9H1', 'A7Z9L0',
    'H5U7Z9', 'Y7A2C4', 'G2X9A4', 'B3X5M9', 'O9X1K3'
  ];

  // Set the baseURL for API requests based on the current hostname
  const baseURL = window.location.hostname.includes('localhost') || 
                 window.location.hostname.includes('127.0.0.1') 
                 ? 'http://backend.localhost' 
                 : 'https://api.antiquewhiteart.com';

  useEffect(() => {
    // Use SKU-specific keys for localStorage
    const couponCodeKey = `countdownCouponCode_${sku}`;
    const endTimeKey = `countdownEndTime_${sku}`;
    
    // Check if we already have a stored coupon code for this specific SKU
    const storedCouponCode = localStorage.getItem(couponCodeKey);
    
    if (storedCouponCode) {
      // Use the stored coupon code
      setCouponCode(storedCouponCode);
    } else {
      // Generate a new random coupon code
      const randomIndex = Math.floor(Math.random() * couponCodes.length);
      const newCouponCode = couponCodes[randomIndex];
      setCouponCode(newCouponCode);
      
      // Store the new coupon code in localStorage with SKU-specific key
      localStorage.setItem(couponCodeKey, newCouponCode);
    }
    
    // Check if we have a stored end time for this specific SKU
    let endTime = localStorage.getItem(endTimeKey);
    
    if (!endTime || isNaN(parseInt(endTime))) {
      // If no valid end time is stored, create a new one (1 hour from now)
      const now = new Date().getTime();
      endTime = now + (1 * 60 * 60 * 1000); // 1 hour in milliseconds
      localStorage.setItem(endTimeKey, endTime);
    } else {
      // Convert stored string to number
      endTime = parseInt(endTime);
    }
    
    // Initial time calculation
    const now = new Date().getTime();
    const remaining = endTime - now;
    
    if (remaining <= 0) {
      // Countdown already finished
      setIsVisible(false);
      return;
    }
    
    setTimeLeft(remaining);
    
    // Set up interval to update countdown
    const interval = setInterval(() => {
      const currentTime = new Date().getTime();
      const newRemaining = endTime - currentTime;
      
      if (newRemaining <= 0) {
        // Countdown finished
        clearInterval(interval);
        setIsVisible(false);
      } else {
        setTimeLeft(newRemaining);
      }
    }, 1000);
    
    // Clean up interval
    return () => clearInterval(interval);
  }, [sku]); // Add sku to dependency array so effect runs when SKU changes
  
  // Format the time display
  const formatTimeUnit = (value) => {
    return value.toString().padStart(2, '0');
  };
  
  const calculateTimeUnits = (milliseconds) => {
    if (!milliseconds) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    return {
      days: days,
      hours: hours % 24,
      minutes: minutes % 60,
      seconds: seconds % 60
    };
  };
  
  // Handle closing the banner
  const handleClose = () => {
    setIsVisible(false);
  };
  
  // Handle copying the coupon code with tracking
  const handleCopyCode = async () => {
    navigator.clipboard.writeText(couponCode);
    setOpenSnackbar(true);
    
    // Track coupon code copy event
    try {
      await axios.post(`${baseURL}/files/track`, { 
        action_type: 'banner_coupon', 
        file_name: couponCode 
      }, { 
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error tracking coupon copy:', error);
    }
  };

  // Handle tracking for Print Now button click
  const handlePrintNowClick = async () => {
    try {
      await axios.post(`${baseURL}/files/track`, { 
        action_type: 'banner_btn_click', 
        file_name: sku 
      }, { 
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Error tracking print button click:', error);
    }
  };
  
  // Handle closing the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };
  
  if (!isVisible) return null;
  
  const timeUnits = calculateTimeUnits(timeLeft);
  
  return (
    <Paper 
      elevation={3}
      sx={{
        backgroundColor: '#E7E4DF',
        color: '#333',
        padding: 2,
        marginBottom: 2,
        marginTop: 2,
        borderRadius: 1,
        position: 'relative',
        textAlign: 'center'
      }}
    >
      {/* Close button */}
      <IconButton 
        aria-label="close" 
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'rgba(0, 0, 0, 0.54)'
        }}
      >
        <CloseIcon />
      </IconButton>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Countdown Timer */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          width: '100%',
          mb: 1
        }}>
          {/* Days */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mx: 1
          }}>
            <Typography 
              component="span"
              sx={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#7C6F5E', // Updated color
                lineHeight: 1
              }}
            >
              {formatTimeUnit(timeUnits.days)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
              days
            </Typography>
          </Box>
          
          {/* Hours */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mx: 1
          }}>
            <Typography 
              component="span"
              sx={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#7C6F5E',
                lineHeight: 1
              }}
            >
              {formatTimeUnit(timeUnits.hours)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
              hours
            </Typography>
          </Box>
          
          {/* Minutes */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mx: 1
          }}>
            <Typography 
              component="span"
              sx={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#7C6F5E',
                lineHeight: 1
              }}
            >
              {formatTimeUnit(timeUnits.minutes)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
              minutes
            </Typography>
          </Box>
          
          {/* Seconds */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mx: 1
          }}>
            <Typography 
              component="span"
              sx={{ 
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#7C6F5E',
                lineHeight: 1
              }}
            >
              {formatTimeUnit(timeUnits.seconds)}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
              seconds
            </Typography>
          </Box>
        </Box>
        
        {/* Your lucky promo code text */}
        <Typography variant="body1" sx={{ mb: 1, fontWeight: 'medium', color: 'black' }}>
            YOUR COUPON CODE:
        </Typography>
        
        {/* Coupon code with dotted border - now clickable */}
        <Box 
          onClick={handleCopyCode}
          sx={{ 
            border: '2px dashed #7C6F5E',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mb: 1,
            maxWidth: '80%',
            mx: 'auto',
            cursor: 'pointer', // Show cursor pointer on hover
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(124, 111, 94, 0.1)' // Light hover effect
            }
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              letterSpacing: 1
            }}
          >
            {couponCode}
          </Typography>
          {/* <ContentCopyIcon 
            sx={{ 
              ml: 1,
              color: '#7C6F5E', 
              fontSize: '1.2rem'
            }} 
          /> */}
        </Box>
        
        {/* Message */}
        <Typography variant="body2" sx={{ mt: 1, mb: 2, maxWidth: '600px', mx: 'auto', fontStyle: 'italic' }}>
            You've unlocked 40% off our premium printing: archival matte, fine art paper, or canvas.
        </Typography>
        
        {/* Button with tracking */}
        <Button 
          variant="contained" 
          sx={{ 
            backgroundColor: '#7C6F5E', 
            color: 'white',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#A19179',
            },
            px: 4,
            py: 1,
            borderRadius: 0
          }}
          href="https://antiquewhiteart.etsy.com/listing/1892000460"
          target="_blank"
          onClick={handlePrintNowClick}
        >
          Print Now
        </Button>
      </Box>
      
      {/* Snackbar for copy notification */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={`Coupon code ${couponCode} copied to clipboard`}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Paper>
  );
};

export default CountdownBanner;